/**
 * API Client Configuration
 *
 * HTTP client setup with axios for auth-service integration
 * Handles JWT tokens, request/response interceptors, and error handling
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { ApiClientConfig, ApiError, TokenData, RefreshTokenResponse, UserInfoResponse } from './types';
import { env } from '../../utils/env';
import { toast } from 'sonner';

// Default configuration
const DEFAULT_CONFIG: ApiClientConfig = {
  // VITE_API_URL é opcional no schema de env; sem ela o axios usa caminho
  // relativo, que é o comportamento esperado quando o app é servido junto da API
  baseURL: env.VITE_API_URL ?? '',
  timeout: 60000,
  withCredentials: false,
};

// Storage keys
const TOKEN_STORAGE_KEY = 'cantina_pay_tokens';
const USER_DATA_KEY = 'cantina_pay_user_info';

// Auth routes used by API client (to avoid circular dependencies with authService)
const AUTH_ROUTES = {
  REFRESH: '/auth/refresh',
} as const;

/**
 * Config das requisições.
 *
 * `requireAuth` é lido pelo interceptor (config as any).requireAuth para
 * decidir se injeta o Bearer, mas não constava na assinatura dos métodos:
 * quem passava `requireAuth: false` — o caminho público, como reset de senha —
 * tomava erro de tipo apesar de a opção funcionar.
 */
export type CpayRequestConfig = AxiosRequestConfig & {
  skipToast?: boolean;
  requireAuth?: boolean;
};

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  constructor(config: Partial<ApiClientConfig> = {}) {
    const clientConfig = { ...DEFAULT_CONFIG, ...config };

    this.client = axios.create(clientConfig);

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Check if this request requires authentication
        const requireAuth = (config as any).requireAuth !== false;

        if (requireAuth) {
          const tokens = this.getStoredTokens();

          // Use stored token or fallback to test token from environment
          const accessToken = tokens?.access_token || import.meta.env.VITE_TEST_TOKEN;

          if (accessToken && config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        // Set content type for JSON requests
        if (config.data && !config.headers?.['Content-Type']) {
          config.headers!['Content-Type'] = 'application/json';
        }

        return config;
      },
      (error) => {
        return Promise.reject(this.handleError(error));
      },
    );

    // Response interceptor - Handle token refresh and toast notifications
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Show success toast for 2XX responses (unless explicitly disabled)
        const skipToast = (response.config as any).skipToast;
        if (!skipToast && response.status >= 200 && response.status < 300) {
          // Try to get success message from response
          const message = response.data?.message || response.data?.data?.message;
          if (message && typeof message === 'string') {
            toast.success(message);
          }
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const tokens = this.getStoredTokens();
            if (tokens?.refresh_token) {
              const response = await this.refreshToken(tokens.refresh_token);

              if (response.access_token) {
                this.storeTokens({
                  access_token: response.access_token,
                  refresh_token: response.refresh_token,
                  session_id: response.session_id,
                  user_id: response.user_id,
                });

                // Process failed queue
                this.processQueue(null);

                return this.client(originalRequest);
              }
            }
          } catch (refreshError) {
            this.processQueue(refreshError);
            this.clearAuth();
            return Promise.reject(this.handleError(refreshError));
          } finally {
            this.isRefreshing = false;
          }
        }

        // Show error toast only for actual errors (but not for specific services that handle their own errors)
        if (process.env.NODE_ENV === 'development') {
          console.log('🚨 API Error:', error.response?.status, error.config?.url, error.response?.data);
        }

        // Don't show toast for:
        // - 401 (authentication errors)
        // - 402 (saldo insuficiente - handled by CartContext)
        // - 409 (estoque insuficiente - handled by CartContext)
        // - Requests with skipErrorToast or skipToast flag
        // - Requests to /transaction/totem/debit (handled by CartContext)
        // These are handled by the calling code with more specific messages
        const status = error.response?.status;
        const skipErrorToast = (originalRequest as any)?.skipErrorToast || (originalRequest as any)?.skipToast;
        const isCheckoutRequest = originalRequest?.url?.includes('/transaction/totem/debit');

        if (!skipErrorToast && !isCheckoutRequest && status !== 401 && status !== 402 && status !== 409) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erro ao fazer a requisição';
          // Verificar se a mensagem não é sobre estoque ou saldo
          const messageStr = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
          if (!messageStr.includes('estoque') && !messageStr.includes('saldo') && !messageStr.includes('insuficiente')) {
            toast.error(errorMessage);
          }
        }

        return Promise.reject(this.handleError(error));
      },
    );
  }

  private processQueue(error: unknown): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(undefined);
      }
    });

    this.failedQueue = [];
  }

  private async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    // Use a separate axios instance to avoid interceptor loops
    const refreshClient = axios.create({
      baseURL: this.client.defaults.baseURL,
      timeout: this.client.defaults.timeout,
    });

    const response = await refreshClient.post<RefreshTokenResponse>(AUTH_ROUTES.REFRESH, {
      refresh_token: refreshToken,
    });

    return response.data;
  }

  private handleError(error: any): ApiError {
    if (axios.isAxiosError(error)) {
      const apiError: ApiError = {
        name: 'ApiError',
        message: error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown API error',
        status: error.response?.status || 0,
        code: error.response?.data?.code || error.code,
        response: error.response,
      };
      return apiError;
    }

    return {
      name: 'ApiError',
      message: error instanceof Error ? error.message : 'Unknown error',
      status: 0,
    };
  }

  // Token management methods
  public storeTokens(tokens: TokenData): void {
    try {
      // Calculate expiration time (access token expires in 15 minutes)
      const expiresAt = Date.now() + 15 * 60 * 1000; // 15 minutes from now
      const tokenData = {
        ...tokens,
        expires_at: expiresAt,
      };

      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  }

  public getStoredTokens(): TokenData | null {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored) {
        const tokens: TokenData = JSON.parse(stored);

        // Check if access token is expired
        if (tokens.expires_at && Date.now() > tokens.expires_at) {
          console.log('Access token expired, refresh needed');
        }

        return tokens;
      }
    } catch (error) {
      console.error('Error getting stored tokens:', error);
    }
    return null;
  }

  public storeUserData(userData: UserInfoResponse): void {
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    } catch (error) {
      console.error('Error storing user data:', error);
    }
  }

  public getStoredUserData(): UserInfoResponse | null {
    try {
      const stored = localStorage.getItem(USER_DATA_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error getting stored user data:', error);
    }
    return null;
  }

  public clearAuth(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    // Clear establishment selection to prevent cross-user data leakage
    localStorage.removeItem('current_establishment');
    // Clear legacy auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('user_info');
    // Note: We don't clear all localStorage to preserve other app data
    // Only authentication-related data is removed
  }

  public isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    return !!(tokens?.access_token && tokens?.refresh_token);
  }

  // HTTP methods
  public async get<T = unknown>(url: string, config?: CpayRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  public async post<T = unknown>(url: string, data?: unknown, config?: CpayRequestConfig): Promise<T> {
    // Limpar campos undefined/null/vazios antes de enviar (apenas para stock/reserve)
    if (url.includes('/stock/reserve') && data && typeof data === 'object') {
      const cleanedData = JSON.parse(JSON.stringify(data, (key, value) => {
        if (value === undefined || value === null || value === '') {
          return undefined; // Remove o campo
        }
        return value;
      }));
      const response = await this.client.post<T>(url, cleanedData, config);
      return response.data;
    }
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T = unknown>(url: string, data?: unknown, config?: CpayRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async patch<T = unknown>(url: string, data?: unknown, config?: CpayRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  public async delete<T = unknown>(url: string, config?: CpayRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  // Get the underlying axios instance for advanced usage
  public getClient(): AxiosInstance {
    return this.client;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export the class for custom instances
export { ApiClient };
export default apiClient;
